import { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  FileText,
  Home,
  LogOut,
  Menu,
  ReceiptText,
  UserCircle2,
  Wrench,
  X,
} from 'lucide-react';
import { OwlLogo } from '../ui/OwlLogo';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/cn';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Главная', icon: Home },
  { to: '/files', label: 'История', icon: FileText },
  { to: '/tools', label: 'Инструменты', icon: Wrench },
  { to: '/tariffs', label: 'Тарифы', icon: ReceiptText },
  { to: '/profile', label: 'Профиль', icon: UserCircle2 },
];

export function NavBar() {
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) {
      firstLinkRef.current?.focus();
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
    return undefined;
  }, [open]);

  return (
    <header className="sticky top-0 z-30 border-b border-stroke bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <Link to="/dashboard" className="flex items-center gap-2">
          <OwlLogo size={36} />
          <span className="text-[17px] font-semibold text-text">OnlyRead</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-bg-soft text-brand-primary'
                    : 'text-text-muted hover:text-text',
                )
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          {user && (
            <span className="text-sm text-text-muted" title={user.email ?? ''}>
              {user.name}
            </span>
          )}
          <button
            type="button"
            onClick={() => void logout()}
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-stroke bg-white px-3 text-sm text-text hover:border-brand-primary"
          >
            <LogOut size={14} />
            Выйти
          </button>
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-bg-soft md:hidden"
          aria-label="Открыть меню"
        >
          <Menu size={18} />
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="absolute right-0 top-0 flex h-full w-80 max-w-[85%] flex-col gap-2 bg-white p-4 shadow-lifted"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-2">
              <span className="text-base font-semibold">Меню</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-soft"
                aria-label="Закрыть меню"
              >
                <X size={18} />
              </button>
            </div>
            {NAV_ITEMS.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                ref={i === 0 ? firstLinkRef : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-[14px] px-3 py-3 text-[15px] font-medium tap-safe',
                    isActive
                      ? 'bg-bg-soft text-brand-primary'
                      : 'text-text hover:bg-bg-soft',
                  )
                }
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            ))}
            <div className="mt-auto border-t border-stroke pt-3">
              {user && (
                <p className="mb-2 px-1 text-sm text-text-muted">
                  {user.name}
                  {user.email ? ` · ${user.email}` : ''}
                </p>
              )}
              <button
                type="button"
                onClick={() => void logout()}
                className="flex w-full items-center justify-center gap-2 rounded-[14px] border border-stroke py-3 text-sm font-medium text-text hover:border-brand-primary tap-safe"
              >
                <LogOut size={16} />
                Выйти
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
